import { initCenterElementOnItself } from '@/commons/fabricjs/FabricPositionHelper';
import { FONT_SIZE_MULTIPLIER } from '@/littleWedding/projectConstantes';
import { StaticCanvas, type CompleteTextStyleDeclaration, type TextStyle } from 'fabric';
import type { IFabricTextCustom } from '@/typescript/interfaces/IFabricTextCustom';
import type { ITextboxCustom } from '@/typescript/interfaces/ITextboxCustom';
import type { ETextAlignement } from '@/typescript/enums/ETextAlignement';
import type { IFabricImageCustom } from '@/typescript/interfaces/IFabricImageCustom';

// Minimal shape shared by FabricText / Textbox needed to move character styles at object level
interface IStyledText {
    set: (options: Record<string, unknown>) => unknown;
    styles?: TextStyle;
}

function applyTextDefaultConfig(fText: IFabricTextCustom | ITextboxCustom /* , alignCenter = true */) {
    fText.setControlsVisibility({
        // m stand for middle, then t is top, b is bottom, l is left, r is right
        mt: false,
        mb: false,
        ml: false,
        mr: false,
        mtr: true, // rotation handle
    });
}

function updateText(
    fabricCanvas: StaticCanvas | null,
    fText: IFabricTextCustom | ITextboxCustom | null,
    text: string,
    alignCenter = true,
): IFabricTextCustom | ITextboxCustom | null {
    if (!fabricCanvas || !fText) {
        return null;
    }
    const centerPosition = initCenterElementOnItself(fText);
    fText.set('text', text);
    if (alignCenter) {
        centerPosition(fText);
    }
    fabricCanvas?.renderAll();
    fText.setCoords();
    return fText;
}

function updateFontFamily({
    alignCenter = true,
    fabricCanvas,
    fontFamily,
    fText,
    initial = false,
    selectedTextIndexes,
}: {
    alignCenter?: boolean;
    fabricCanvas: StaticCanvas | null;
    fontFamily: string;
    fText?: IFabricImageCustom | IFabricTextCustom | ITextboxCustom;
    initial?: boolean;
    selectedTextIndexes?: { end: number; start: number };
}): IFabricTextCustom | ITextboxCustom | null {
    if (!fabricCanvas || !fText || 'text' in fText === false) {
        return null;
    }

    if (selectedTextIndexes) {
        fText.setSelectionStyles({ fontFamily: fontFamily }, selectedTextIndexes.start, selectedTextIndexes.end);
    } else {
        if (!initial) {
            removeCharacterLevelStyling(fText, 'fontFamily');
        }

        fText.set('fontFamily', fontFamily);
    }
    const centerPosition = initCenterElementOnItself(fText);

    if (alignCenter) {
        centerPosition(fText);
    }
    fText.set('dirty', true);
    fabricCanvas?.renderAll();
    fText.setCoords();
    return fText;
}

function updateFontSize({
    alignCenter = true,
    fabricCanvas,
    fontSize,
    fText,
    selectedTextIndexes,
}: {
    alignCenter?: boolean;
    fabricCanvas: StaticCanvas | null;
    fontSize: number;
    fText?: IFabricImageCustom | IFabricTextCustom | ITextboxCustom;
    selectedTextIndexes?: { end: number; start: number };
}): IFabricTextCustom | ITextboxCustom | null {
    if (!fabricCanvas || !fText || 'text' in fText === false) {
        return null;
    }

    if (selectedTextIndexes) {
        fText.setSelectionStyles({ fontSize: fontSize * FONT_SIZE_MULTIPLIER }, selectedTextIndexes.start, selectedTextIndexes.end);
    } else {
        removeCharacterLevelStyling(fText, 'fontSize');

        fText.set('fontSize', fontSize * FONT_SIZE_MULTIPLIER);
    }
    const centerPosition = initCenterElementOnItself(fText);
    if (alignCenter) {
        centerPosition(fText);
    }

    fText.set('dirty', true);

    fabricCanvas?.renderAll();
    fText.setCoords();
    return fText;
}

/**
 * Update color of font
 * @param fontColor hexa color string
 */
function updateFontColor({
    fabricCanvas,
    fontColor,
    fText,
    selectedTextIndexes,
}: {
    fabricCanvas: StaticCanvas | null;
    fontColor: string;
    fText?: IFabricImageCustom | IFabricTextCustom | ITextboxCustom;
    selectedTextIndexes?: { end: number; start: number };
}): IFabricTextCustom | ITextboxCustom | null {
    if (!fabricCanvas || !fText || 'text' in fText === false) {
        return null;
    }

    if (selectedTextIndexes) {
        fText.setSelectionStyles({ fill: fontColor }, selectedTextIndexes.start, selectedTextIndexes.end);
    } else {
        removeCharacterLevelStyling(fText, 'fill');
        fText.set('fill', fontColor);
    }
    fText.set('dirty', true);
    fabricCanvas?.renderAll();

    return fText;
}

function updateFontWeight({
    alignCenter = true,
    fabricCanvas,
    fontWeight = '300',
    fText,
    selectedTextIndexes,
}: {
    alignCenter?: boolean;
    fabricCanvas: StaticCanvas | null;
    fontWeight: '300' | '700';
    fText?: IFabricImageCustom | IFabricTextCustom | ITextboxCustom;
    selectedTextIndexes?: { end: number; start: number };
}): IFabricTextCustom | ITextboxCustom | null {
    if (!fabricCanvas || !fText || 'text' in fText === false) {
        return null;
    }
    const centerPosition = initCenterElementOnItself(fText);

    if (selectedTextIndexes) {
        fText.setSelectionStyles({ fontWeight: fontWeight }, selectedTextIndexes.start, selectedTextIndexes.end);
    } else {
        removeCharacterLevelStyling(fText, 'fontWeight');
        fText.set('fontWeight', fontWeight);
    }
    fText.set('dirty', true);
    if (alignCenter) {
        centerPosition(fText);
    }
    fabricCanvas?.renderAll();
    fText.setCoords();

    return fText;
}

function updateFontStyle({
    alignCenter = true,
    fabricCanvas,
    fontStyle = 'normal',
    fText,
    selectedTextIndexes,
}: {
    alignCenter?: boolean;
    fabricCanvas: StaticCanvas | null;
    fontStyle: 'italic' | 'normal';
    fText?: IFabricImageCustom | IFabricTextCustom | ITextboxCustom;
    selectedTextIndexes?: { end: number; start: number };
}): IFabricTextCustom | ITextboxCustom | null {
    if (!fabricCanvas || !fText || 'text' in fText === false) {
        return null;
    }
    const centerPosition = initCenterElementOnItself(fText);

    if (selectedTextIndexes) {
        fText.setSelectionStyles({ fontStyle: fontStyle }, selectedTextIndexes.start, selectedTextIndexes.end);
    } else {
        removeCharacterLevelStyling(fText, 'fontStyle');
        fText.set('fontStyle', fontStyle);
    }

    if (alignCenter) {
        centerPosition(fText);
    }
    fText.set('dirty', true);
    fabricCanvas?.renderAll();
    fText.setCoords();

    return fText;
}

function updateUnderline({
    fabricCanvas,
    fText,
    selectedTextIndexes,
    underline,
}: {
    fabricCanvas: StaticCanvas | null;
    fText?: IFabricImageCustom | IFabricTextCustom | ITextboxCustom;
    selectedTextIndexes?: { end: number; start: number };
    underline: boolean;
}): IFabricTextCustom | ITextboxCustom | null {
    if (!fabricCanvas || !fText || 'text' in fText === false) {
        return null;
    }

    if (selectedTextIndexes) {
        fText.setSelectionStyles({ underline, textDecorationThickness: 40 }, selectedTextIndexes.start, selectedTextIndexes.end);
    } else {
        removeCharacterLevelStyling(fText, 'underline');
        fText.set('underline', underline);
        fText.set('textDecorationThickness', 40); // TODO: you should have a number between 0 and 100 that depends on the font size, default 66
    }

    fText.set('dirty', true);
    fabricCanvas?.renderAll();
    return fText;
}

function updateAngle(fabricCanvas: StaticCanvas | null, fText: IFabricTextCustom | ITextboxCustom | null, angle: number) {
    if (!fabricCanvas || !fText) {
        return;
    }
    fText.rotate(angle);
    fabricCanvas?.renderAll();
    fText.setCoords();
}

function textScaleToFontsize(fText: IFabricTextCustom | ITextboxCustom | null) {
    if (!fText) return;
    const { fontSize, left, scaleX, scaleY, top } = fText;
    const newFontSize = Math.round(fontSize * Math.min(scaleX, scaleY));
    fText.set('fontSize', newFontSize);
    fText.set('scaleX', 1);
    fText.set('scaleY', 1);
    fText.set('left', left);
    fText.set('top', top);
    fText.setCoords();
    fText.set('dirty', true);
}

function updateAlignment(
    fabricCanvas: StaticCanvas | null,
    fText: IFabricTextCustom | ITextboxCustom | null,
    newAlignement: ETextAlignement,
) {
    if (!fText) return;
    const { textAlign } = fText;
    fText.set('textAlign', newAlignement ?? textAlign);
    fText.set('dirty', true);
    fabricCanvas?.renderAll();
}

function updateLineHeight(fabricCanvas: StaticCanvas | null, fText: IFabricTextCustom | ITextboxCustom | null, newLineSpacing: number) {
    if (!fText) return;
    const lineSpacingOffset = 16 / (FONT_SIZE_MULTIPLIER * 4);
    const refineLineSpacingOffset = (newLineSpacing === 0 ? 0.01 : newLineSpacing / (FONT_SIZE_MULTIPLIER * 4)) + lineSpacingOffset;
    fText.set('lineHeight', refineLineSpacingOffset === 0 ? 0.001 : refineLineSpacingOffset);
    fText.set('dirty', true);
    fabricCanvas?.renderAll();
}

function updateCharSpacing(fabricCanvas: StaticCanvas | null, fText: IFabricTextCustom | ITextboxCustom | null, newCharSpacing: number) {
    if (!fText) return;
    fText.set('charSpacing', newCharSpacing * FONT_SIZE_MULTIPLIER * 4);
    fText.set('dirty', true);
    fabricCanvas?.renderAll();
}

function removeCharacterLevelStyling(fText: IFabricTextCustom | ITextboxCustom, key: keyof CompleteTextStyleDeclaration) {
    if (fText.styles) {
        Object.entries(fText.styles).forEach(([line, chars]) => {
            Object.entries(chars).forEach(([char, style]) => {
                if (key in style) {
                    delete style[key];

                    // Clean up empty style objects
                    if (Object.keys(style).length === 0) {
                        delete chars[char];
                    }
                }
            });

            // Clean up empty line style arrays
            if (Object.keys(chars).length === 0) {
                delete fText.styles[line];
            }
        });
    }

    return fText;
}

/**
 * Copy the style of the first character (line 0, char 0 - unwrapped coordinates) at object level.
 * Meant to be called right before fabric drops every character level style (text fully deleted),
 * so the look of the text survives on the next typed characters.
 */
function promoteFirstCharacterStyle<T extends IStyledText>(fText: T): T {
    const firstCharacterStyle = fText.styles?.[0]?.[0];
    if (!firstCharacterStyle || Object.keys(firstCharacterStyle).length === 0) {
        return fText;
    }
    fText.set({ ...firstCharacterStyle });

    return fText;
}

/**
 * Clicking a toolbar control moves the DOM focus from the hidden textarea of the text being edited to
 * the clicked button: fabric stops the cursor animation, clears the selection highlight and the next
 * key strokes are lost. Give the focus back and redraw the cursor / selection.
 */
function restoreTextEditingFocus(fText: IFabricTextCustom | ITextboxCustom) {
    if (!('isEditing' in fText) || !fText.isEditing) {
        return;
    }
    fText.hiddenTextarea?.focus({ preventScroll: true });
    fText.initDelayedCursor(true);
}

export {
    applyTextDefaultConfig,
    promoteFirstCharacterStyle,
    restoreTextEditingFocus,
    textScaleToFontsize,
    updateAlignment,
    updateAngle,
    updateCharSpacing,
    updateFontColor,
    updateFontFamily,
    updateFontSize,
    updateFontStyle,
    updateFontWeight,
    updateLineHeight,
    updateText,
    updateUnderline,
};
export type { IStyledText };
